import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';

export interface AccountHealthScore {
  organization_id: string;
  overall_score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  health_status: 'excellent' | 'good' | 'fair' | 'poor' | 'critical';
  factors: {
    engagement: number;
    financial: number;
    communication: number;
    satisfaction: number;
    growth: number;
  };
  recommendations: string[];
  last_calculated: string;
  trend: 'improving' | 'stable' | 'declining';
}

export interface HealthMetrics {
  recent_activities: number;
  revenue_trend: number;
  payment_history: number;
  communication_frequency: number;
  project_success_rate: number;
  contract_renewal_rate: number;
  support_tickets: number;
  satisfaction_scores: number[];
}

class AccountHealthService {
  async calculateHealthScore(organizationId: string): Promise<AccountHealthScore> {
    try {
      const metrics = await this.getHealthMetrics(organizationId);
      const factors = this.calculateFactorScores(metrics);
      const overallScore = this.calculateOverallScore(factors);
      const riskLevel = this.determineRiskLevel(overallScore, factors);
      const healthStatus = this.determineHealthStatus(overallScore);
      const recommendations = this.generateRecommendations(factors, metrics);
      const trend = await this.calculateTrend(organizationId);

      const healthScore: AccountHealthScore = {
        organization_id: organizationId,
        overall_score: overallScore,
        risk_level: riskLevel,
        health_status: healthStatus,
        factors,
        recommendations,
        last_calculated: new Date().toISOString(),
        trend,
      };

      // Store the calculated score
      await this.saveHealthScore(healthScore);

      return healthScore;
    } catch (error) {
      logError('Error calculating health score:', error);
      throw error;
    }
  }

  private async getHealthMetrics(organizationId: string): Promise<HealthMetrics> {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();

    // Recent activities
    const { data: activities } = await supabase
      .from('activities')
      .select('*')
      .eq('organization_id', organizationId)
      .gte('created_at', thirtyDaysAgo);

    // Revenue data
    const { data: invoices } = await supabase
      .from('invoices')
      .select('total_amount, created_at, status')
      .eq('organization_id', organizationId)
      .gte('created_at', ninetyDaysAgo);

    // Communication frequency
    const { data: communications } = await supabase
      .from('communications')
      .select('*')
      .eq('organization_id', organizationId)
      .gte('created_at', thirtyDaysAgo);

    // Project data
    const { data: projects } = await supabase
      .from('projects')
      .select('*')
      .eq('organization_id', organizationId);

    // Support tickets or issues
    const { data: tickets } = await supabase
      .from('activities')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('type', 'support')
      .gte('created_at', thirtyDaysAgo);

    // Calculate metrics
    const recentRevenue =
      invoices
        ?.filter((inv) => inv.status === 'paid' && inv.created_at >= thirtyDaysAgo)
        ?.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) || 0;

    const previousRevenue =
      invoices
        ?.filter((inv) => inv.status === 'paid' && inv.created_at < thirtyDaysAgo)
        ?.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) || 0;

    const revenueTrend =
      previousRevenue > 0 ? ((recentRevenue - previousRevenue) / previousRevenue) * 100 : 0;

    const completedProjects = projects?.filter((p) => p.status === 'completed') || [];
    const projectSuccessRate =
      projects?.length > 0 ? (completedProjects.length / projects.length) * 100 : 0;

    const paidInvoices = invoices?.filter((inv) => inv.status === 'paid') || [];
    const paymentHistory =
      invoices?.length > 0 ? (paidInvoices.length / invoices.length) * 100 : 100;

    return {
      recent_activities: activities?.length || 0,
      revenue_trend: revenueTrend,
      payment_history: paymentHistory,
      communication_frequency: communications?.length || 0,
      project_success_rate: projectSuccessRate,
      contract_renewal_rate: 100, // This would need contract data
      support_tickets: tickets?.length || 0,
      satisfaction_scores: [85, 90, 88], // Mock data - would come from surveys
    };
  }

  private calculateFactorScores(metrics: HealthMetrics) {
    // Engagement Score (0-100)
    const engagementScore = Math.min(
      100,
      metrics.recent_activities * 10 + metrics.communication_frequency * 5
    );

    // Financial Score (0-100)
    const financialScore = Math.min(
      100,
      metrics.payment_history * 0.6 + Math.max(0, Math.min(100, metrics.revenue_trend + 50)) * 0.4
    );

    // Communication Score (0-100)
    const communicationScore = Math.min(100, metrics.communication_frequency * 8);

    // Satisfaction Score (0-100)
    const satisfactionScore =
      metrics.satisfaction_scores.length > 0
        ? metrics.satisfaction_scores.reduce((sum, score) => sum + score, 0) /
          metrics.satisfaction_scores.length
        : 75;

    // Growth Score (0-100)
    const growthScore = Math.min(
      100,
      Math.max(
        0,
        metrics.project_success_rate * 0.4 + Math.max(0, metrics.revenue_trend + 50) * 0.6
      )
    );

    return {
      engagement: Math.round(engagementScore),
      financial: Math.round(financialScore),
      communication: Math.round(communicationScore),
      satisfaction: Math.round(satisfactionScore),
      growth: Math.round(growthScore),
    };
  }

  private calculateOverallScore(factors: AccountHealthScore['factors']): number {
    const weights = {
      engagement: 0.2,
      financial: 0.3,
      communication: 0.15,
      satisfaction: 0.2,
      growth: 0.15,
    };

    const weightedScore =
      factors.engagement * weights.engagement +
      factors.financial * weights.financial +
      factors.communication * weights.communication +
      factors.satisfaction * weights.satisfaction +
      factors.growth * weights.growth;

    return Math.round(weightedScore);
  }

  private determineRiskLevel(
    overallScore: number,
    factors: AccountHealthScore['factors']
  ): AccountHealthScore['risk_level'] {
    // Critical risk indicators
    if (factors.financial < 30 || factors.satisfaction < 40 || overallScore < 40) {
      return 'critical';
    }

    if (overallScore < 60 || factors.financial < 50) {
      return 'high';
    }

    if (overallScore < 75) {
      return 'medium';
    }

    return 'low';
  }

  private determineHealthStatus(overallScore: number): AccountHealthScore['health_status'] {
    if (overallScore >= 85) return 'excellent';
    if (overallScore >= 70) return 'good';
    if (overallScore >= 55) return 'fair';
    if (overallScore >= 40) return 'poor';
    return 'critical';
  }

  private generateRecommendations(
    factors: AccountHealthScore['factors'],
    metrics: HealthMetrics
  ): string[] {
    const recommendations: string[] = [];

    if (factors.engagement < 60) {
      recommendations.push('Schedule regular check-ins to increase engagement');
      recommendations.push('Plan strategic account review meeting');
    }

    if (factors.financial < 60) {
      recommendations.push('Review payment terms and collection process');
      if (metrics.revenue_trend < -10) {
        recommendations.push('Investigate revenue decline and expansion opportunities');
      }
    }

    if (factors.communication < 50) {
      recommendations.push('Increase communication frequency');
      recommendations.push('Set up regular newsletter or updates');
    }

    if (factors.satisfaction < 70) {
      recommendations.push('Conduct satisfaction survey');
      recommendations.push('Address any outstanding service issues');
    }

    if (factors.growth < 60) {
      recommendations.push('Explore upsell and cross-sell opportunities');
      recommendations.push('Review project success metrics');
    }

    if (metrics.support_tickets > 5) {
      recommendations.push('Address recurring support issues');
    }

    return recommendations;
  }

  private async calculateTrend(organizationId: string): Promise<AccountHealthScore['trend']> {
    try {
      // Get previous scores to determine trend
      const { data: previousScores } = await supabase
        .from('account_health_scores')
        .select('overall_score, created_at')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(3);

      if (!previousScores || previousScores.length < 2) {
        return 'stable';
      }

      const scores = previousScores.map((s) => s.overall_score);
      const recentScore = scores[0];
      const olderScore = scores[scores.length - 1];

      if (recentScore > olderScore + 5) return 'improving';
      if (recentScore < olderScore - 5) return 'declining';
      return 'stable';
    } catch (error) {
      logError('Error calculating trend:', error);
      return 'stable';
    }
  }

  private async saveHealthScore(healthScore: AccountHealthScore): Promise<void> {
    try {
      await supabase.from('account_health_scores').insert({
        organization_id: healthScore.organization_id,
        overall_score: healthScore.overall_score,
        risk_level: healthScore.risk_level,
        health_status: healthScore.health_status,
        engagement_score: healthScore.factors.engagement,
        financial_score: healthScore.factors.financial,
        communication_score: healthScore.factors.communication,
        satisfaction_score: healthScore.factors.satisfaction,
        growth_score: healthScore.factors.growth,
        recommendations: healthScore.recommendations,
        trend: healthScore.trend,
        created_at: healthScore.last_calculated,
      });
    } catch (error) {
      logError('Error saving health score:', error);
    }
  }

  async getHealthScore(organizationId: string): Promise<AccountHealthScore | null> {
    try {
      const { data, error } = await supabase
        .from('account_health_scores')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return {
        organization_id: data.organization_id,
        overall_score: data.overall_score,
        risk_level: data.risk_level,
        health_status: data.health_status,
        factors: {
          engagement: data.engagement_score,
          financial: data.financial_score,
          communication: data.communication_score,
          satisfaction: data.satisfaction_score,
          growth: data.growth_score,
        },
        recommendations: data.recommendations || [],
        last_calculated: data.created_at,
        trend: data.trend,
      };
    } catch (error) {
      logError('Error fetching health score:', error);
      return null;
    }
  }

  async getAllHealthScores(): Promise<(AccountHealthScore & { organization_name: string })[]> {
    try {
      const { data, error } = await supabase
        .from('account_health_scores')
        .select(
          `
          *,
          organizations!inner(name)
        `
        )
        .order('overall_score', { ascending: true });

      if (error) throw error;

      return (
        data?.map((item) => ({
          organization_id: item.organization_id,
          organization_name: item.organizations.name,
          overall_score: item.overall_score,
          risk_level: item.risk_level,
          health_status: item.health_status,
          factors: {
            engagement: item.engagement_score,
            financial: item.financial_score,
            communication: item.communication_score,
            satisfaction: item.satisfaction_score,
            growth: item.growth_score,
          },
          recommendations: item.recommendations || [],
          last_calculated: item.created_at,
          trend: item.trend,
        })) || []
      );
    } catch (error) {
      logError('Error fetching all health scores:', error);
      return [];
    }
  }

  async getAtRiskAccounts(): Promise<(AccountHealthScore & { organization_name: string })[]> {
    try {
      const allScores = await this.getAllHealthScores();
      return allScores.filter(
        (score) => score.risk_level === 'high' || score.risk_level === 'critical'
      );
    } catch (error) {
      logError('Error fetching at-risk accounts:', error);
      return [];
    }
  }

  async refreshAllHealthScores(): Promise<void> {
    try {
      const { data: organizations } = await supabase.from('organizations').select('id');

      if (organizations) {
        await Promise.all(organizations.map((org) => this.calculateHealthScore(org.id)));
      }
    } catch (error) {
      logError('Error refreshing all health scores:', error);
    }
  }
}

export const accountHealthService = new AccountHealthService();
